import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Identidade do atendente que está no aparelho agora (PIN). Um PDV de balcão
// costuma ficar logado o dia inteiro na mesma conta; sem isso, todas as vendas
// do salão são atribuídas a ela e o fechamento por operador vira ficção.
// Ausente = o backend atribui ao usuário logado, exatamente como antes.
const OPERATOR_KEY = "oplyx_operator_token";
const OPERATOR_NAME_KEY = "oplyx_operator_name";

export const getOperatorName = () => localStorage.getItem(OPERATOR_NAME_KEY);

export const setOperator = (token, name) => {
  localStorage.setItem(OPERATOR_KEY, token);
  localStorage.setItem(OPERATOR_NAME_KEY, name || "");
  try { window.dispatchEvent(new CustomEvent("operator-changed", { detail: { name } })); } catch (_) {}
};

export const clearOperator = () => {
  localStorage.removeItem(OPERATOR_KEY);
  localStorage.removeItem(OPERATOR_NAME_KEY);
  try { window.dispatchEvent(new CustomEvent("operator-changed", { detail: { name: null } })); } catch (_) {}
};

/**
 * Executa `fn` como um operador específico, sem trocar quem está no aparelho.
 * Usado quando o gerente digita o PIN só para autorizar UMA ação: sem isso ele
 * ficaria como operador e as vendas seguintes do atendente sairiam no nome dele.
 */
export const runAsOperator = async (operatorToken, fn) => {
  const anterior = localStorage.getItem(OPERATOR_KEY);
  localStorage.setItem(OPERATOR_KEY, operatorToken);
  try {
    return await fn();
  } finally {
    if (anterior) localStorage.setItem(OPERATOR_KEY, anterior);
    else localStorage.removeItem(OPERATOR_KEY);
  }
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("oplyx_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const branch = localStorage.getItem("oplyx_branch");
  if (branch) config.headers["X-Branch-Id"] = branch;
  const operator = localStorage.getItem(OPERATOR_KEY);
  if (operator) config.headers["X-Operator-Token"] = operator;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (e) => {
    // Turno do operador expirado (~14h) NÃO é sessão expirada: a conta segue
    // logada, só a identificação por PIN caiu. Derrubar o login aqui tiraria o
    // PDV do ar no meio do expediente.
    if (e?.response?.status === 401 && e?.response?.data?.detail?.code === "operator_token_invalid") {
      clearOperator();
      return Promise.reject(e);
    }
    if (e?.response?.status === 401) {
      localStorage.removeItem("oplyx_token");
      const p = window.location.pathname;
      if (!p.includes("/auth") && !p.startsWith("/j/")) {
        window.location.href = p.startsWith("/staff") ? "/staff/auth" : "/auth";
      }
    }
    // 402 = lifecycle gate (pending/demo expired/suspended/cancelled). Dispatch global event.
    if (e?.response?.status === 402) {
      const detail = e?.response?.data?.detail || "";
      try { window.dispatchEvent(new CustomEvent("tenant-blocked", { detail: { message: detail } })); } catch (_) {}
    }
    // 428 = ação sensível precisa da autorização do responsável legal
    // (backend/legal_approval.py). O LegalApprovalPrompt acompanha o pedido;
    // aprovado, a mesma chamada é refeita com X-Legal-Approval e quem chamou
    // recebe a resposta como se nada tivesse acontecido.
    if (e?.response?.status === 428 && e?.response?.data?.detail?.code === "legal_approval_required") {
      const d = e.response.data.detail;
      return new Promise((resolve, reject) => {
        const giveUp = (msg) => {
          const err = new Error(msg);
          err.response = { status: 428, data: { detail: msg } };
          reject(err);
        };
        const resume = (approved, msg) => {
          if (!approved) return giveUp(msg || "A ação não foi feita: falta a autorização do responsável legal.");
          const h = e.config.headers;
          const headers = { ...(h && typeof h.toJSON === "function" ? h.toJSON() : h), "X-Legal-Approval": d.approval_id };
          api.request({ ...e.config, headers }).then(resolve, reject);
        };
        try { window.dispatchEvent(new CustomEvent("legal-approval-required", { detail: { ...d, resume } })); }
        catch (_) { giveUp(d.message); }
      });
    }
    // Feature-blocked 403 → dispatch global event so UpgradePrompt modal can show
    if (e?.response?.status === 403) {
      const detail = e?.response?.data?.detail || "";
      const match = /Recurso '([^']+)'/.exec(detail);
      if (match) {
        try { window.dispatchEvent(new CustomEvent("feature-blocked", { detail: { feature: match[1], message: detail } })); } catch (_) {}
      }
    }
    return Promise.reject(e);
  }
);

export const brl = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
/** Número em pt-BR com casas fixas: numBr(9.9) -> "9,9" (vírgula decimal, ponto de milhar). */
export const numBr = (n, digits = 1) =>
  (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Percentual em pt-BR: pctBr(9.94) -> "9,9%". */
export const pctBr = (n, digits = 1) => `${numBr(n, digits)}%`;


/**
 * Ciclo de cobrança — espelha billing_lib.is_yearly no backend.
 * Convivem "month"/"year" (subscription.cycle) e "monthly"/"yearly"
 * (billing_cycle, checkout). Comparar direto com uma das grafias fazia a UI
 * mostrar "Mensal" para cliente anual. Nunca compare a string na tela.
 */
export const isYearly = (cycle) =>
  ["year", "yearly", "annual", "anual"].includes(String(cycle || "").toLowerCase());
export const cycleLabel = (cycle) => (isYearly(cycle) ? "Anual" : "Mensal");
export const cycleSuffix = (cycle) => (isYearly(cycle) ? "ano" : "mês");

/**
 * Format ISO date string (YYYY-MM-DD or full ISO) into pt-BR display DD/MM/YYYY.
 * Returns "" for falsy input. Falls back to raw value if invalid.
 */
export const dateBr = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/** Format YYYY-MM period into MM/YYYY (pt-BR). */
export const periodBr = (p) => {
  if (!p) return "";
  const m = String(p).match(/^(\d{4})-(\d{2})$/);
  return m ? `${m[2]}/${m[1]}` : p;
};

/** Returns true if the error is a feature-gate 403 (UpgradePrompt modal is already handling it). */
export const isFeatureBlocked = (err) => {
  if (err?.response?.status !== 403) return false;
  const detail = err?.response?.data?.detail || "";
  return /^Recurso '/.test(detail);
};

/** Show a toast only if the error is NOT already handled by UpgradePrompt. */
export const errToast = (err, fallback = "Algo deu errado") => {
  if (isFeatureBlocked(err)) return; // UpgradePrompt modal handles it
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // Pydantic validation errors come as a list of {loc, msg, type}
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : "";
    const msg = first.msg || "Campo inválido";
    toast.error(field ? `${field}: ${msg}` : msg);
    return;
  }

  if (typeof detail === "string" && detail.trim()) {
    toast.error(detail);
    return;
  }

  // Detail como objeto estruturado (ex: needs_pack_size com lista de itens)
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const msg = detail.message || detail.code || "";
    const items = Array.isArray(detail.items) ? detail.items : null;
    if (msg && items && items.length) {
      const lines = items.slice(0, 3).map(i =>
        `• Linha ${i.line ?? "?"}: ${i.name ?? ""} (${i.invoice_unit ?? "?"} → ${i.base_unit ?? "?"})`
      ).join("\n");
      const more = items.length > 3 ? `\n…e mais ${items.length - 3}` : "";
      toast.error(`${msg}\n${lines}${more}`, { duration: 8000 });
      return;
    }
    if (msg) { toast.error(msg); return; }
  }

  // Generic HTTP guidance
  if (status === 401) return toast.error("Sessão expirada. Entre novamente para continuar.");
  if (status === 403) return toast.error("Você não tem permissão para esta ação.");
  if (status === 404) return toast.error("Não encontrado. O item pode ter sido removido.");
  if (status === 409) return toast.error("Conflito: já existe um item com esses dados.");
  if (status === 422) return toast.error("Dados inválidos. Revise os campos e tente novamente.");
  if (status === 429) return toast.error("Muitas tentativas. Aguarde alguns segundos.");
  if (status && status >= 500) return toast.error("Servidor indisponível. Tente novamente em instantes.");
  if (err?.message === "Network Error") return toast.error("Sem conexão. Verifique sua internet.");

  toast.error(fallback);
};
